import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createTeamLimit, listTeamLimits, removeTeamLimit, updateTeamLimitAmount } from '@/api/onsiteLiveTeams';
import { ApiError } from '@/api/client';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { formatMoney } from '@/lib/dateFormat';
import type { OnsiteLiveTeam, TeamLimit, TeamLimits } from '@/types/oscar';
import { ErrorBanner, FieldError } from '../BranchRelationships/shared';
import { btnPrimary, btnSecondary, inputCls } from '../BranchRelationships/styles';
import { LIMITS_KEY, TEAMS_KEY } from './keys';
import { CATEGORY_CREDIT, CATEGORY_PO, emptyLimit, limitSchema, parseAmount, toLimitPayload, type LimitFormData } from './schema';
import { TeamCombobox } from './TeamCombobox';

/** Full width of the Add limit card; min-w-0 so a long option can't push the select past the card edge. */
const fieldCls = `${inputCls} mt-1 w-full min-w-0 font-normal`;

/** What each category drives, so the admin knows where a change lands. */
function categoryNote(categoryId: number): string {
  if (categoryId === CATEGORY_PO)
    return 'PO limits. OnFlow uses them for PO approval at once; the SQ06 job copies them to Baseplan (PO_User_Limit) for every user in the team at 20:00.';
  if (categoryId === CATEGORY_CREDIT)
    return 'Credit invoice limit. The SQ06 job copies it to Baseplan (Users.CreditInvoiceLimit) at 20:00.';
  return 'Used by OnFlow approvals.';
}

/**
 * Limits (oscar.TeamLimit = Onsitelive.DAL.TeamLimit): an amount per team ×
 * category × line type × sub-line type. One category at a time; a whole number of
 * dollars, 0 = no authority. Every change is audited.
 */
export function LimitsTab({ teams, canWrite }: { teams: OnsiteLiveTeam[]; canWrite: boolean }) {
  const query = useQuery({ queryKey: LIMITS_KEY, queryFn: ({ signal }) => listTeamLimits(signal) });
  const data = query.data;
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [teamFilter, setTeamFilter] = useState('');

  useEffect(() => {
    if (categoryId === null && data?.Categories.length) {
      setCategoryId(data.Categories.some((c) => c.CategoryId === CATEGORY_PO) ? CATEGORY_PO : data.Categories[0].CategoryId);
    }
  }, [data, categoryId]);

  const teamById = useMemo(() => new Map(teams.map((t) => [t.TeamId, t])), [teams]);
  const rows = useMemo(
    () => (data?.Limits ?? [])
      .filter((l) => l.CategoryId === categoryId && (teamFilter === '' || String(l.TeamId) === teamFilter))
      .sort((a, b) => a.TeamId - b.TeamId || (a.LineTypeId ?? 0) - (b.LineTypeId ?? 0) || (a.SubLineTypeId ?? 0) - (b.SubLineTypeId ?? 0)),
    [data, categoryId, teamFilter],
  );

  if (query.isLoading) return <p className="text-sm text-darkGrey/60">Loading…</p>;
  if (query.isError || !data) return <ErrorBanner message={(query.error as Error | null)?.message || 'Could not load the limits.'} />;

  return (
    <div>
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Limit category">
        {data.Categories.map((c) => (
          <button
            key={c.CategoryId}
            type="button"
            role="radio"
            aria-checked={categoryId === c.CategoryId}
            onClick={() => setCategoryId(c.CategoryId)}
            className={categoryId === c.CategoryId
              ? 'rounded-full bg-navy px-3 py-1 text-xs font-semibold text-white'
              : 'rounded-full border border-border bg-white px-3 py-1 text-xs font-semibold text-darkGrey hover:text-navy'}
          >
            {c.Description}{c.CategoryId === CATEGORY_PO ? ' (PO limits)' : ''}
          </button>
        ))}
      </div>
      {categoryId !== null && <p className="mt-2 text-sm text-darkGrey/80">{categoryNote(categoryId)}</p>}

      <div className="mt-3 flex flex-col gap-4 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1">
          <label className="mb-2 flex w-full max-w-sm flex-col text-xs font-semibold text-darkGrey">
            Team
            <TeamCombobox teams={teams} value={teamFilter} onChange={setTeamFilter} emptyLabel="All teams" ariaLabel="Filter by team" className="mt-1" />
          </label>
          <div className="overflow-x-auto rounded border border-border bg-white">
            <table className="w-full text-sm">
              <thead className="bg-navy text-left text-xs text-white">
                <tr>
                  <th className="px-3 py-2">Team</th>
                  <th className="px-3 py-2">Line type</th>
                  <th className="px-3 py-2">Sub-line type</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  {canWrite && <th className="px-3 py-2"><span className="sr-only">Actions</span></th>}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-3 text-darkGrey/60">No limits in this category{teamFilter ? ' for this team' : ''}.</td></tr>
                )}
                {rows.map((l, i) => (
                  <LimitRow key={l.TeamLimitId} limit={l} data={data} team={teamById.get(l.TeamId)} canWrite={canWrite} striped={i % 2 === 1} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {canWrite && categoryId !== null && (
          <div className="min-w-0 lg:sticky lg:top-4 lg:w-80 lg:shrink-0">
            <AddLimit data={data} teams={teams} categoryId={categoryId} />
          </div>
        )}
      </div>
    </div>
  );
}

function lineLabel(data: TeamLimits, id: number | null) {
  if (id === null) return '—';
  const t = data.LineTypes.find((x) => x.LineTypeId === id);
  return t ? `${t.Code}${t.Description ? ` — ${t.Description}` : ''}` : `#${id}`;
}

function subLineLabel(data: TeamLimits, id: number | null) {
  if (id === null) return '—';
  const t = data.SubLineTypes.find((x) => x.SubLineTypeId === id);
  return t ? `${t.Code}${t.Description ? ` — ${t.Description}` : ''}` : `#${id}`;
}

function LimitRow({
  limit, data, team, canWrite, striped,
}: {
  limit: TeamLimit;
  data: TeamLimits;
  team: OnsiteLiveTeam | undefined;
  canWrite: boolean;
  striped: boolean;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState(limit.Amount === null ? '' : String(limit.Amount));
  const [confirmRemove, setConfirmRemove] = useState(false);
  useEffect(() => { setText(limit.Amount === null ? '' : String(limit.Amount)); }, [limit.Amount]);

  const amount = parseAmount(text);
  const dirty = amount !== null && amount !== limit.Amount;
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: LIMITS_KEY });
    void qc.invalidateQueries({ queryKey: TEAMS_KEY });
  };
  const save = useMutation({ mutationFn: (value: number) => updateTeamLimitAmount(limit.TeamLimitId, value), onSuccess: refresh });
  const remove = useMutation({ mutationFn: () => removeTeamLimit(limit.TeamLimitId), onSuccess: () => { setConfirmRemove(false); refresh(); } });
  const label = `${team?.TeamCode ?? `Team ${limit.TeamId}`} ${subLineLabel(data, limit.SubLineTypeId)}`;

  return (
    <tr className={striped ? 'bg-[#F4F6F9]' : 'bg-white'}>
      <td className="px-3 py-1.5">
        <span className="font-mono text-xs text-darkGrey/60">{limit.TeamId}</span> {team?.TeamCode ?? ''}
        {team && <span className="block text-xs text-darkGrey/60">{team.ActiveUsers} active users</span>}
      </td>
      <td className="px-3 py-1.5 text-xs">{lineLabel(data, limit.LineTypeId)}</td>
      <td className="px-3 py-1.5 text-xs">{subLineLabel(data, limit.SubLineTypeId)}</td>
      <td className="px-3 py-1.5 text-right">
        {canWrite ? (
          <>
            <input
              aria-label={`Amount for ${label}`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              inputMode="numeric"
              className={`${inputCls} w-32 text-right`}
            />
            {amount === null && <span className="block text-xs text-red">Whole dollars, 0 or more</span>}
            {save.isError && <span role="alert" className="block text-xs text-red">{save.error instanceof ApiError ? save.error.message : 'Not saved.'}</span>}
          </>
        ) : (
          formatMoney(limit.Amount)
        )}
      </td>
      {canWrite && (
        <td className="whitespace-nowrap px-3 py-1.5 text-right">
          <button type="button" className={btnSecondary} disabled={!dirty || save.isPending} onClick={() => amount !== null && save.mutate(amount)}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>{' '}
          <button type="button" className={btnSecondary} onClick={() => { remove.reset(); setConfirmRemove(true); }} aria-label={`Remove limit ${label}`}>
            Remove
          </button>
          {confirmRemove && (
            <AlertDialog
              open
              onOpenChange={(o) => { if (!o) setConfirmRemove(false); }}
              title="Remove this limit?"
              description={`${label}: ${formatMoney(limit.Amount)}. Without a row the team has no authority for it. The row is kept (soft delete) and the change is audited.`}
              confirmLabel="Remove"
              destructive
              closeOnConfirm={false}
              onConfirm={() => remove.mutate()}
            >
              {remove.isError && <p role="alert" className="text-sm text-red">{remove.error instanceof ApiError ? remove.error.message : 'Could not remove it.'}</p>}
            </AlertDialog>
          )}
        </td>
      )}
    </tr>
  );
}

function AddLimit({ data, teams, categoryId }: { data: TeamLimits; teams: OnsiteLiveTeam[]; categoryId: number }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const { register, control, handleSubmit, formState, reset, watch, setValue } = useForm<LimitFormData>({
    resolver: zodResolver(limitSchema),
    defaultValues: emptyLimit,
  });
  const activeTeams = useMemo(() => teams.filter((t) => t.IsActive), [teams]);
  const lineTypeId = watch('LineTypeId');
  const subLines = data.SubLineTypes.filter((s) => String(s.LineTypeId) === lineTypeId);
  useEffect(() => { setValue('SubLineTypeId', ''); }, [lineTypeId, setValue]);

  const add = useMutation({
    mutationFn: (d: LimitFormData) => createTeamLimit(toLimitPayload(limitSchema.parse(d), categoryId)),
    onSuccess: () => {
      setError(null);
      reset(emptyLimit);
      void qc.invalidateQueries({ queryKey: LIMITS_KEY });
      void qc.invalidateQueries({ queryKey: TEAMS_KEY });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not add the limit.'),
  });
  const err = formState.errors;

  return (
    <form
      className="rounded border border-border bg-white p-4"
      aria-label="Add limit"
      onSubmit={(e) => { e.preventDefault(); void handleSubmit((d) => add.mutate(d))(); }}
    >
      <h2 className="font-heading text-base font-bold text-navy">Add limit</h2>
      <fieldset disabled={add.isPending} className="mt-3 space-y-3">
        <label className="flex min-w-0 flex-col text-xs font-semibold text-darkGrey">
          Team
          <Controller
            control={control}
            name="TeamId"
            render={({ field }) => (
              <TeamCombobox teams={activeTeams} value={field.value} onChange={field.onChange} className="mt-1" />
            )}
          />
          <FieldError message={err.TeamId?.message} />
        </label>
        <label className="flex min-w-0 flex-col text-xs font-semibold text-darkGrey">
          Line type
          <select {...register('LineTypeId')} className={fieldCls}>
            <option value="">— none —</option>
            {data.LineTypes.map((t) => <option key={t.LineTypeId} value={String(t.LineTypeId)}>{t.Code}{t.Description ? ` — ${t.Description}` : ''}</option>)}
          </select>
        </label>
        <label className="flex min-w-0 flex-col text-xs font-semibold text-darkGrey">
          Sub-line type
          <select {...register('SubLineTypeId')} disabled={lineTypeId === ''} className={`${fieldCls} disabled:bg-[#F4F6F9] disabled:text-darkGrey/50`}>
            <option value="">{lineTypeId === '' ? 'Pick a line type first' : '— none —'}</option>
            {subLines.map((s) => <option key={s.SubLineTypeId} value={String(s.SubLineTypeId)}>{s.Code}{s.Description ? ` — ${s.Description}` : ''}</option>)}
          </select>
        </label>
        <label className="flex min-w-0 flex-col text-xs font-semibold text-darkGrey">
          Amount ($)
          <input {...register('Amount')} inputMode="numeric" placeholder="e.g. 50,000" className={fieldCls} />
          <FieldError message={err.Amount?.message} />
        </label>
      </fieldset>
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      <button type="submit" className={`${btnPrimary} mt-4`} disabled={add.isPending}>{add.isPending ? 'Adding…' : 'Add'}</button>
    </form>
  );
}
